import { Avatar } from 'antd';
import React, { useState } from 'react';
import { UserOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { formatRelativeTime, COMMENT_TIMESTAMP_FORMAT } from '../../../../common/dateUtils';
import { getUserProfilePictureUrl } from '../../../../common/userHelper';
import colorTokens from '../../../../constants/design_system/colors';
import { TaskComment } from '../../../../types/Comment';
import { User } from '../../../../types/User';
import { RichTextEditor } from '../editor/RichTextEditor';
import { BaseAttachments } from '../BaseAttachments';
import CustomTooltip from '../../../../components/UI/Tooltip/CustomTooltip';
import { DeleteCommentModal } from '../../../../components/Modal/DeleteCommentModal';
import { CommentToolbar } from './CommentToolbar';
import { CommentReactions } from './CommentReactions';
import { useToggleCommentReaction } from '../../../../serverState/tasks';

interface CommentProps {
  comment: TaskComment;
  viewer?: {
    id: string;
    email?: string;
    fullName?: string | null;
  };
  onEdit?: (commentId: number, content: string) => void;
  onDelete?: (commentId: number) => void;
  onReply?: () => void;
  usersWithAccessToSpace?: Array<{
    id: number;
    fullName?: string | null;
    email: string;
    profilePicturePath?: string | null;
  }>;
}

export const Comment: React.FC<CommentProps> = ({
  comment,
  viewer,
  onEdit,
  onDelete,
  onReply,
  usersWithAccessToSpace = [],
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(comment.content);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const isOwner = viewer?.id === comment.User.id.toString();
  const currentUserId = viewer ? Number(viewer.id) : undefined;

  const toggleReaction = useToggleCommentReaction(comment.taskId);

  const reactions = comment.Reactions ?? [];
  const viewerReactedEmojis = new Set(reactions.filter((r) => r.userId === currentUserId).map((r) => r.emoji));

  const handleToggleReaction = (emoji: string) => {
    toggleReaction(comment.id, emoji, viewerReactedEmojis.has(emoji));
  };

  const handleEditClick = () => {
    setIsEditing(true);
    setEditContent(comment.content);
  };

  const handleSaveEdit = () => {
    if (onEdit) {
      onEdit(comment.id, editContent);
    }
    setIsEditing(false);
  };

  const handleEditorBlur = () => {
    setIsEditing(false);
  };

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    if (onDelete) {
      onDelete(comment.id);
    }
    setShowDeleteModal(false);
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
  };

  return (
    <div className="w-full bg-neutral-100 border-b border-neutral-200 p-4 rounded-lg group">
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <Avatar
            icon={<UserOutlined />}
            size={24}
            style={{ backgroundColor: colorTokens.neutral[400], marginRight: '5px' }}
            src={getUserProfilePictureUrl(comment.User.profilePicturePath)}
          />
          <span className="text-sm font-semibold">{comment.User.fullName || comment.User.email}</span>
          <CustomTooltip title={dayjs(comment.createdAt).format(COMMENT_TIMESTAMP_FORMAT)}>
            <span className="text-xs text-neutral-900 ml-1 cursor-default">
              {formatRelativeTime(comment.createdAt)}
              {comment.createdAt !== comment.updatedAt && ' (edited)'}
            </span>
          </CustomTooltip>
        </div>

        <CommentToolbar
          isOwner={isOwner}
          viewerReactedEmojis={viewerReactedEmojis}
          onToggleReaction={handleToggleReaction}
          onReply={onReply}
          onEdit={handleEditClick}
          onDelete={handleDeleteClick}
        />
      </div>

      {isEditing ? (
        <div className="mt-2">
          <RichTextEditor
            value={editContent}
            onChange={setEditContent}
            onSubmit={handleSaveEdit}
            onBlur={handleEditorBlur}
            usersWithAccessToSpace={usersWithAccessToSpace}
            showSubmitButton={true}
            submitButtonText=""
            showCancelButton={true}
            cancelButtonText=""
            onCancel={handleEditorBlur}
            mode="comment"
            minHeight="60px"
            autoFocus={true}
            middleContent={
              <div className="px-4 pt-1">
                <BaseAttachments attachments={comment.CommentAttachments} onDeleteAttachment={() => undefined} />
              </div>
            }
          />
        </div>
      ) : (
        <>
          <RichTextEditor
            value={comment.content}
            onChange={() => undefined}
            onSubmit={() => undefined}
            usersWithAccessToSpace={usersWithAccessToSpace}
            showSubmitButton={false}
            disabled={true}
            mode="comment"
            minHeight="20px"
          />
          <BaseAttachments
            attachments={comment.CommentAttachments}
            onDeleteAttachment={() => undefined}
            allowDelete={false}
          />
          <CommentReactions reactions={reactions} currentUserId={currentUserId} onToggle={handleToggleReaction} />
        </>
      )}

      <DeleteCommentModal
        comment={comment}
        open={showDeleteModal}
        onOk={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </div>
  );
};
